export function parseAllocationImport(content, assets) {
  const assetIds = collectAssetIds(content, assets);
  const percentages = collectPercentages(content);
  const totalBasisPoints = percentages.reduce((sum, basisPoints) => sum + basisPoints, 0);
  const stats = { assetCount: assetIds.length, percentageCount: percentages.length, totalBasisPoints };

  if (assetIds.length === 0) throw importError("No known asset IDs were found.", stats);
  if (assetIds.length !== percentages.length) {
    throw importError("The number of asset IDs and percentages does not match.", stats);
  }

  const seen = new Set();
  const allocations = assetIds.map((assetId, index) => {
    if (seen.has(assetId)) throw importError(`Asset “${assetId}” appears more than once.`, stats);
    seen.add(assetId);
    return { assetId, basisPoints: percentages[index] };
  });

  return { allocations, ...stats };
}

export function adjustAllocationsToTotal(allocations, targetBasisPoints = 10000) {
  const total = allocations.reduce((sum, allocation) => sum + allocation.basisPoints, 0);
  if (total === 0) throw new Error("A zero allocation cannot be adjusted to 100%.");

  const scaled = allocations.map((allocation, index) => {
    const exactBasisPoints = allocation.basisPoints * targetBasisPoints / total;
    return { ...allocation, basisPoints: Math.floor(exactBasisPoints), remainder: exactBasisPoints % 1, index };
  });
  const remaining = targetBasisPoints - scaled.reduce((sum, allocation) => sum + allocation.basisPoints, 0);
  const remainderOrder = [...scaled].sort((left, right) => right.remainder - left.remainder || left.index - right.index);
  for (let index = 0; index < remaining; index += 1) remainderOrder[index].basisPoints += 1;

  return scaled.map(({ assetId, basisPoints }) => ({ assetId, basisPoints }));
}

export function sortAllocationsDescending(allocations) {
  return allocations.map((allocation, index) => ({ allocation, index }))
    .sort((left, right) => right.allocation.basisPoints - left.allocation.basisPoints || left.index - right.index)
    .map(({ allocation }) => allocation);
}

function collectAssetIds(content, assets) {
  if (assets.length === 0) return [];
  const alternatives = assets.map((asset) => escapeRegExp(asset.id)).sort((left, right) => right.length - left.length);
  const pattern = new RegExp(alternatives.join("|"), "gu");
  const found = [];

  for (const match of content.matchAll(pattern)) {
    const start = match.index;
    const end = start + match[0].length;
    if (isAssetIdCharacter(previousCharacter(content, start)) || isAssetIdCharacter(nextCharacter(content, end))) continue;
    found.push(match[0]);
  }
  return found;
}

function collectPercentages(content) {
  return [...content.matchAll(/[+-]?(?:\d+(?:\.\d+)?|\.\d+)\s*%/g)].map((match) => {
    const raw = match[0].replace(/\s*%$/, "");
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) {
      throw new Error(`Invalid percentage “${match[0].trim()}”. Use a value from 0% to 100% with up to two decimal places.`);
    }
    const basisPoints = Math.round(Number(raw) * 100);
    if (basisPoints > 10000) {
      throw new Error(`Invalid percentage “${match[0].trim()}”. Use a value from 0% to 100% with up to two decimal places.`);
    }
    return basisPoints;
  });
}

function importError(message, stats) {
  return Object.assign(new Error(message), { stats });
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function previousCharacter(content, index) {
  return index === 0 ? "" : [...content.slice(0, index)].at(-1);
}

function nextCharacter(content, index) {
  return index === content.length ? "" : [...content.slice(index)][0];
}

function isAssetIdCharacter(value) {
  return value ? /[\p{L}\p{N}_]/u.test(value) : false;
}
